import { CheckCircle2, AlertTriangle } from 'lucide-react';
import type { TafireData } from '../../../types/reports';
import { CurrencyCode } from '../../../config/currency';

interface TafireStatementProps {
  data: TafireData;
  currency?: CurrencyCode;
}

/**
 * TAFIRE — Tableau Financier des Ressources et Emplois (SYSCOHADA Normal).
 *
 * Affiche les 4 grandes sections :
 *  - CAFG (Capacité d'AutoFinancement Globale)
 *  - Variation du FRNG (Fonds de Roulement Net Global)
 *  - Variation du BFR (Besoin en Fonds de Roulement)
 *  - Variation de la Trésorerie nette
 *
 * Avec vérification de l'identité comptable : ΔFRNG = ΔBFR + ΔTrésorerie
 */
export function TafireStatement({ data, currency = 'CDF' }: TafireStatementProps) {
  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  return (
    <div className="space-y-6">
      {/* Bandeau identité comptable */}
      <div
        className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
          data.identity.isBalanced
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
        }`}
      >
        {data.identity.isBalanced ? (
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <p className="font-medium">
            Identité comptable : ΔFRNG = ΔBFR + ΔTrésorerie
          </p>
          <p className="text-xs font-mono mt-1">
            {fmt(data.identity.variationFrng)} = {fmt(data.bfr.variation)} +{' '}
            {fmt(data.tresorerie.variation)}
            {!data.identity.isBalanced && (
              <span className="ml-2">(écart : {fmt(data.identity.ecart)} {currency})</span>
            )}
          </p>
        </div>
      </div>

      {/* Section 1 — CAFG */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">
          1. CAFG — Capacité d'AutoFinancement Globale
        </h3>
        <table className="w-full text-sm">
          <tbody>
            <Row label="Résultat net de l'exercice" amount={data.cafg.resultatNet} />
            <Row label="+ Dotations aux amortissements et provisions (68)" amount={data.cafg.dotationsAmortissements} />
            <Row label="− Reprises sur amortissements et provisions (78)" amount={-data.cafg.reprisesAmortissements} />
            <Row label="+ VNC des cessions d'immobilisations (81)" amount={data.cafg.vncCessionsImmo} />
            <Row label="− Produits de cession des immobilisations (82)" amount={-data.cafg.produitsCessionsImmo} />
            <TotalRow label="= CAFG" amount={data.cafg.total} />
          </tbody>
        </table>
      </section>

      {/* Section 2 — FRNG */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">
          2. FRNG — Fonds de Roulement Net Global
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs">
              <th className="py-1.5">Composante</th>
              <th className="py-1.5 text-right">Ouverture</th>
              <th className="py-1.5 text-right">Clôture</th>
            </tr>
          </thead>
          <tbody>
            <DualRow
              label="Capitaux permanents (classe 1)"
              open={data.frng.capitauxPermanentsOpen}
              close={data.frng.capitauxPermanentsClose}
            />
            <DualRow
              label="− Actif immobilisé net (classes 2 − 28)"
              open={-data.frng.immobiliseNetOpen}
              close={-data.frng.immobiliseNetClose}
            />
            <DualTotalRow label="= FRNG" open={data.frng.frngOpen} close={data.frng.frngClose} />
            <VariationRow label="Variation du FRNG" variation={data.frng.variation} />
          </tbody>
        </table>
      </section>

      {/* Section 3 — BFR */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">
          3. BFR — Besoin en Fonds de Roulement
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs">
              <th className="py-1.5">Composante</th>
              <th className="py-1.5 text-right">Ouverture</th>
              <th className="py-1.5 text-right">Clôture</th>
            </tr>
          </thead>
          <tbody>
            <DualRow label="Stocks (classe 3)" open={data.bfr.stocksOpen} close={data.bfr.stocksClose} />
            <DualRow
              label="+ Créances clients (41)"
              open={data.bfr.creancesClientsOpen}
              close={data.bfr.creancesClientsClose}
            />
            <DualRow
              label="− Dettes fournisseurs (40)"
              open={-data.bfr.dettesFournisseursOpen}
              close={-data.bfr.dettesFournisseursClose}
            />
            <DualRow
              label="− Autres dettes courantes (42-44)"
              open={-data.bfr.autresDettesCourantesOpen}
              close={-data.bfr.autresDettesCourantesClose}
            />
            <DualTotalRow label="= BFR" open={data.bfr.bfrOpen} close={data.bfr.bfrClose} />
            <VariationRow label="Variation du BFR" variation={data.bfr.variation} />
          </tbody>
        </table>
      </section>

      {/* Section 4 — Trésorerie */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">
          4. Trésorerie Nette
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs">
              <th className="py-1.5">Composante</th>
              <th className="py-1.5 text-right">Ouverture</th>
              <th className="py-1.5 text-right">Clôture</th>
            </tr>
          </thead>
          <tbody>
            <DualTotalRow
              label="Trésorerie (classe 5)"
              open={data.tresorerie.tresorerieOpen}
              close={data.tresorerie.tresorerieClose}
            />
            <VariationRow label="Variation de la trésorerie" variation={data.tresorerie.variation} />
          </tbody>
        </table>
      </section>

      <p className="text-xs text-muted-foreground italic pt-2 border-t border-border">
        Devise : {currency} — Référentiel : {data.metadata.standard} ({data.metadata.version}) — Généré le{' '}
        {new Date(data.metadata.generatedAt).toLocaleString('fr-FR')}
      </p>
    </div>
  );
}

// ── Sous-composants tabulaires ──

function Row({ label, amount }: { label: string; amount: number }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-2 pr-4">{label}</td>
      <td className="py-2 text-right font-mono">
        {new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(amount)}
      </td>
    </tr>
  );
}

function TotalRow({ label, amount }: { label: string; amount: number }) {
  return (
    <tr className="border-t-2 border-foreground/40">
      <td className="py-2 pr-4 font-semibold">{label}</td>
      <td className="py-2 text-right font-mono font-semibold">
        {new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(amount)}
      </td>
    </tr>
  );
}

function DualRow({ label, open, close }: { label: string; open: number; close: number }) {
  const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(n);
  return (
    <tr className="border-b border-border/50">
      <td className="py-2 pr-4">{label}</td>
      <td className="py-2 text-right font-mono">{fmt(open)}</td>
      <td className="py-2 text-right font-mono">{fmt(close)}</td>
    </tr>
  );
}

function DualTotalRow({ label, open, close }: { label: string; open: number; close: number }) {
  const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(n);
  return (
    <tr className="border-t-2 border-foreground/40">
      <td className="py-2 pr-4 font-semibold">{label}</td>
      <td className="py-2 text-right font-mono font-semibold">{fmt(open)}</td>
      <td className="py-2 text-right font-mono font-semibold">{fmt(close)}</td>
    </tr>
  );
}

function VariationRow({ label, variation }: { label: string; variation: number }) {
  const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(n);
  const isPositive = variation >= 0;
  return (
    <tr>
      <td className="py-2 pr-4 italic text-muted-foreground">{label}</td>
      <td
        colSpan={2}
        className={`py-2 text-right font-mono font-semibold ${
          isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
        }`}
      >
        {isPositive ? '+' : ''}
        {fmt(variation)}
      </td>
    </tr>
  );
}
